import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { ApiTemplate } from '../lib/agent-templates';
import { absolute } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// The account's agent templates as a data table (spec §3.15): name, program +
// backend badges, the model it pins, when it last changed, Edit + Delete.
// Presenter — the route owns the rows and every write.
//
// The endpoint is shown, the TOKEN never is: a compatible backend's credential is
// a box secret and is not a template field at all, so there is nothing here to
// mask. An unset column reads as a dash rather than an empty cell, so "no model
// pinned" is distinguishable from a cell that failed to render.

export function TemplateTable(props: {
  readonly rows: readonly ApiTemplate[];
  readonly onEdit: (template: ApiTemplate) => void;
  readonly onDelete: (template: ApiTemplate) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<ApiTemplate>[] = [
    {
      key: 'name',
      rowHeader: true,
      header: t('account_templates.col_name'),
      mono: true,
      cell: (tp) => tp.name,
    },
    {
      key: 'program',
      header: t('account_templates.col_program'),
      cell: (tp) => <span class="badge badge--outline">{tp.program}</span>,
    },
    {
      key: 'backend',
      header: t('account_templates.col_backend'),
      cell: (tp) => <span class="badge badge--outline">{tp.backend}</span>,
    },
    {
      key: 'model',
      header: t('account_templates.col_model'),
      mono: true,
      cell: (tp) => tp.model ?? UNSET,
    },
    {
      key: 'updated',
      header: t('account_templates.col_updated'),
      cell: (tp) => absolute(tp.updated_iso),
    },
    {
      key: 'actions',
      header: t('account_templates.col_actions'),
      srOnlyHeader: true,
      align: 'end',
      cell: (tp) => (
        <div class="data-table__actions">
          <Button variant="ghost" size="sm" onClick={() => props.onEdit(tp)}>
            {t('account_templates.edit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => props.onDelete(tp)}>
            {t('account_templates.delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      label={t('account_templates.table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(tp) => tp.id}
    />
  );
}
